/**
 * Helpers shared by the schema-definition JSON parsers
 * (extendAISchemaParser / sqlSchemaParser / samplePayloadParser) — name
 * conversion, plain-object guards, and the common paste-input boilerplate.
 */

export function snakeToCamel(input: string): string {
  return input.replace(/_([a-z0-9])/g, (_, char: string) => char.toUpperCase());
}

export function camelToSnake(input: string): string {
  return input
    .replace(/([a-z0-9])([A-Z])/g, '$1_$2')
    .replace(/[\s-]+/g, '_')
    .toLowerCase();
}

export function toDisplayLabel(fieldName: string): string {
  return fieldName
    .replace(/[_-]+/g, ' ')
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .trim()
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

export function isPlainObject(
  value: unknown,
): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

export type JsonObjectInputResult =
  { ok: true; value: Record<string, unknown> } | { ok: false; error: string };

/**
 * The common first step of every paste parser: trim, JSON.parse, and require a
 * plain object at the top level. `emptyMessage` names what the user should
 * paste ("config" / "schema" / "payload").
 */
export function parseJsonObjectInput(
  rawJson: string,
  emptyMessage: string,
): JsonObjectInputResult {
  const trimmed = rawJson.trim();
  if (!trimmed) {
    return { ok: false, error: emptyMessage };
  }

  let parsed: unknown;
  try {
    parsed = JSON.parse(trimmed);
  } catch (err) {
    const detail = err instanceof Error ? err.message : 'unknown error';
    return { ok: false, error: `Invalid JSON: ${detail}` };
  }

  if (!isPlainObject(parsed)) {
    return { ok: false, error: 'Top-level value must be a JSON object.' };
  }

  return { ok: true, value: parsed };
}
